import React from "react";
import { useHistory } from "react-router-dom";
import useIntercom from "@reclaim-ai/react-intercom-hook";
import { AuthContext } from "../../auth/AuthProvider";
import { authService } from "../../util";
import headerBg from "../../images/headerbg.svg";
import { useCompanyContext } from "../companies/useCompanyContext";
import { isApproverReadOnly } from "../commons/useApproverReadOnly";
import { canShowWalletFeature } from "../companies/pdtiFeatureFlags";
import { Avatar, Menu, Logo } from "../../designSystem";
import { EVENTS, useAnalytics } from "../../analytics";
import type { MenuItem } from "../../designSystem/components/navigation/Menu";
import { useBreakpoint } from "../layouts/breakpoints";
import { useEmbeddedWalletMenuItems } from "../../wallet/dynamic/useEmbeddedWalletMenuItems";

interface ILoginHeader {
  className?: string;
  showSidebar?: boolean;
  /** When set, renders a hamburger that opens the mobile nav drawer (AppShell). */
  onOpenMenu?: () => void;
}

const APP_HEADER_HEIGHT = 56;

export const LoginHeader: React.FC<ILoginHeader> = ({
  className,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  showSidebar,
  onOpenMenu,
}) => {
  const { currentUser, invalidateCurrentUser } = React.useContext(AuthContext);
  const { features, displayName: companyDisplayName, companyName, displayLogo, userCompanyRoles } = useCompanyContext();
  // Add company hidden for approvers — reuses the existing investor gating.
  const approverReadOnly = isApproverReadOnly(userCompanyRoles);
  const history = useHistory();
  const intercom = useIntercom();
  const breakpoint = useBreakpoint();
  const embeddedWalletItems = useEmbeddedWalletMenuItems();
  const { track } = useAnalytics();

  const logout = async () => {
    try {
      await authService.signOut();
      track(EVENTS.SIGN_OUT_COMPLETED, { trigger: "user" });
      invalidateCurrentUser();
      intercom("shutdown");
      // eslint-disable-next-line no-empty
    } catch {}
    history.replace("/signin");
  };

  if (!currentUser) {
    return <></>;
  }

  const displayName = currentUser ? `${currentUser.firstName}${" "}${currentUser.familyName}` : "";
  const userInitials = `${currentUser.firstName?.[0] ?? ""}${currentUser.familyName?.[0] ?? ""}`.toUpperCase() || "?";
  const companyLabel = companyDisplayName || companyName;
  const isCurrentPageSwitchCompany = history.location.pathname === "/switch-company";

  const items: MenuItem[] = [
    !isCurrentPageSwitchCompany && {
      id: "switch-company",
      label: "Switch portals",
      testId: "user-menu-switch-company",
      onSelect: () => history.push("/switch-company"),
    },
    !currentUser.isInvestor &&
      !approverReadOnly && {
        id: "add-company",
        label: "Add company",
        testId: "user-menu-add-company",
        onSelect: () => history.push("/add-company"),
      },
    canShowWalletFeature(features) && {
      id: "wallet",
      label: "Wallet",
      onSelect: () => history.push("/wallet"),
    },
    ...embeddedWalletItems,
    {
      id: "manage-account",
      label: "Manage Your Account",
      testId: "user-menu-manage-account",
      onSelect: () => history.push("/account"),
    },
    {
      id: "sign-out",
      label: "Sign Out",
      testId: "user-menu-sign-out",
      onSelect: () => logout(),
    },
  ].filter(Boolean) as MenuItem[];

  const isAppBar = className?.includes("header-fixed");
  const isMobile = breakpoint === "mobile";

  const profileMenu = (
    <Menu
      align="end"
      items={items}
      trigger={(open) => (
        <div data-testid="user-settings-menu" style={{ display: "flex", alignItems: "center", cursor: "pointer" }}>
          <Avatar initials={userInitials} variant="default" size="sm" data-testid="user-settings-avatar" />
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              marginLeft: 10,
              marginRight: 4,
              minWidth: 0,
            }}
          >
            <span
              data-profile-name
              data-testid="user-settings-name"
              className={`u-font-family-body u-font-bold-weight u-whitespace-nowrap ${
                open ? "u-text-success" : "u-text-strong"
              }`}
              style={{
                fontSize: 13,
                lineHeight: "16px",
              }}
            >
              {displayName}
            </span>
            {companyLabel ? (
              <span
                data-profile-company
                data-testid="user-settings-company"
                className={`u-font-family-body u-font-paragraph-weight u-whitespace-nowrap ${
                  open ? "u-text-success" : "u-text-muted"
                }`}
                style={{
                  fontSize: 13,
                  lineHeight: "16px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: 180,
                }}
              >
                {companyLabel}
              </span>
            ) : null}
          </div>
          <svg
            data-testid="user-settings-chevron"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden
            xmlns="http://www.w3.org/2000/svg"
            className={open ? "u-text-success" : "u-text-strong"}
            style={{
              marginLeft: 4,
              transition: "transform .2s ease",
              transform: open ? "rotate(180deg)" : "none",
            }}
          >
            <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
      )}
    />
  );

  if (isAppBar) {
    return (
      <header
        data-testid="app-header"
        className="u-bg-white u-border-b-divider u-px-gutter"
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          height: APP_HEADER_HEIGHT,
          boxSizing: "border-box",
          flex: "none",
        }}
      >
        {onOpenMenu ? (
          <>
            <button
              type="button"
              aria-label="Open navigation menu"
              aria-haspopup="menu"
              onClick={onOpenMenu}
              className="u-text-strong u-radius-xs u-cursor-pointer"
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 44,
                height: 44,
                border: "none",
                background: "transparent",
                flex: "none",
              }}
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                aria-hidden
              >
                <line x1="3" y1="6" x2="21" y2="6" />
                <line x1="3" y1="12" x2="21" y2="12" />
                <line x1="3" y1="18" x2="21" y2="18" />
              </svg>
            </button>
            <Logo variant="mark" height={28} brandSrc={displayLogo} color="var(--green-success)" />
          </>
        ) : null}
        <div style={{ flex: 1, minWidth: 0 }} />
        {profileMenu}
      </header>
    );
  }

  // Legacy slate band for company list and other non-shell flows.
  return (
    <div
      className={`u-text-white u-bg-slate${className ? ` ${className}` : ""}`}
      style={{
        padding: isMobile ? "10px 24px" : "10px 27px",
        backgroundImage: `url(${headerBg})`,
        backgroundSize: "cover",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          maxWidth: "100%",
          height: 36,
          margin: "0 auto",
          width: "100%",
        }}
      >
        {profileMenu}
      </div>
    </div>
  );
};
